/**
 * TopBar (P26 + P30 auth wiring).
 *
 * Crumb on the left. On the right, real signed-in user info + sign-out
 * (replaces the static "Siddharth A" placeholder). The persona/role label
 * comes from /api/v1/me.
 */
import { Fragment, useMemo, useState, type JSX } from 'react';
import { ChevronRight, LogOut, ShieldCheck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext.js';
import { breadcrumbForPath } from './breadcrumb.js';
import { useBreadcrumb } from './BreadcrumbContext.js';

export function TopBar(): JSX.Element {
  const location = useLocation();
  const { me, signOut } = useAuth();
  const { detailLabel } = useBreadcrumb();
  const [open, setOpen] = useState(false);

  const crumb = useMemo(
    () => breadcrumbForPath(location.pathname, detailLabel ?? undefined),
    [location.pathname, detailLabel],
  );

  const initials = useMemo(() => {
    const source = me?.display_name || me?.email || '?';
    const parts = source.split(/[\s@.]+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  }, [me]);

  const role = me?.role ?? 'viewer';

  return (
    <header className="topbar" role="banner">
      <nav className="crumb" aria-label="Breadcrumb">
        <Link
          to="/"
          style={{ color: 'inherit', textDecoration: 'none', opacity: 0.65 }}
          title="Command Center"
        >
          {crumb.group}
        </Link>
        {crumb.crumbs.map((c, i) => {
          const isLast = i === crumb.crumbs.length - 1;
          return (
            <Fragment key={`${i}-${c.label}`}>
              <ChevronRight
                size={12}
                aria-hidden
                style={{ opacity: 0.45, verticalAlign: 'middle' }}
              />
              {c.path && !isLast ? (
                <Link to={c.path} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {c.label}
                </Link>
              ) : (
                <strong>{c.label}</strong>
              )}
            </Fragment>
          );
        })}
      </nav>

      <div className="topbar-actions">
        <span className="action-button" title={`Signed in as ${me?.email ?? '—'}`}>
          <ShieldCheck size={14} style={{ marginRight: 6 }} />
          {role === 'admin' || role === 'bootstrap' ? 'Admin' : 'Viewer'}
        </span>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="action-button avatar"
            aria-label={`Signed in as ${me?.email ?? 'unknown'} (${role})`}
            title={me?.email ?? ''}
            onClick={() => setOpen((o) => !o)}
          >
            {initials}
          </button>
          {open && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 6px)',
                background: 'var(--background, white)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                minWidth: 220,
                padding: 8,
                zIndex: 50,
              }}
            >
              <div style={{ padding: '8px 10px', fontSize: 14 }}>
                <div style={{ color: 'var(--muted-foreground, #888)' }}>Signed in as</div>
                <div style={{ fontWeight: 600 }}>{me?.email ?? '—'}</div>
                <div style={{ color: 'var(--muted-foreground, #888)', marginTop: 4 }}>
                  Role: <code>{role === 'bootstrap' || role === 'admin' ? 'admin' : 'viewer'}</code>
                </div>
              </div>
              <button
                type="button"
                className="topbar-menu-item"
                onClick={() => {
                  setOpen(false);
                  signOut();
                  window.location.reload();
                }}
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
